import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { subscriptionApi, type CancellationReason } from '../services/subscriptionApi';

/**
 * Opens the Stripe Customer Portal in the same tab.
 * Use this only for billing-related side trips (download invoices, update card).
 * In-app cancel goes through {@link useCancelSubscriptionAtPeriodEnd}.
 */
export function useBillingPortal() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: () => subscriptionApi.manageSubscription('billing_portal'),
    onSuccess: (data) => {
      if (data.url) window.location.assign(data.url);
      if (user?.id) queryClient.invalidateQueries({ queryKey: ['subscription', user.id] });
    },
  });
}

/**
 * Cancels the subscription.
 * - Default mode is `cancel_at_period_end` (keeps access until cycle end).
 * - If the subscription is in `past_due`, the backend automatically switches to
 *   immediate cancellation (Spec SOS-334 Edge Case 1).
 * - Optional `reason` is recorded in `subscriptions.cancellation_reason` for analytics.
 */
export function useCancelSubscriptionAtPeriodEnd() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (vars?: { reason?: CancellationReason | null }) =>
      subscriptionApi.manageSubscription({
        action: 'cancel_at_period_end',
        reason: vars?.reason ?? null,
      }),
    onSuccess: () => {
      if (user?.id) queryClient.invalidateQueries({ queryKey: ['subscription', user.id] });
    },
  });
}

/**
 * Resumes a subscription scheduled to cancel at period end.
 * Clears any previously captured cancellation reason — the user changed their mind.
 */
export function useResumeSubscription() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: () => subscriptionApi.manageSubscription('resume'),
    onSuccess: () => {
      if (user?.id) queryClient.invalidateQueries({ queryKey: ['subscription', user.id] });
    },
  });
}
